import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, CardContent, Rating, Skeleton, Typography } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import {
  studentFormsApi,
  type StudentForm,
  type StudentFormAnswerValue,
  type StudentFormField,
} from '@/api/studentForms'
import { languageProfileApi, type StudentLanguageProfile } from '@/api/placement'
import { formatDateDMY } from '@/utils/date'
import { StudentFormEditDialog } from './StudentFormEditDialog'
import { LanguageGoalSection } from './LanguageGoalSection'

interface StudentFormSummaryProps {
  contractId: number
  /** Владелец анкеты: цель и уровень живут в его языковом профиле, а не в договоре. */
  userId: number
  /** Учитель ведёт анкету: правит все поля и смотрит историю правок. */
  canManage: boolean
  /** Отступ сверху: блок встраивается в разные страницы. */
  sx?: object
}

/**
 * Анкета ученика отдельным блоком в профиле — только заполненные поля.
 *
 * Пустые поля не показываются: блок отвечает на вопрос «что известно об
 * ученике», а не «что осталось заполнить». Полный список и правка —
 * в модальном окне, как у договора.
 */
export function StudentFormSummary({ contractId, userId, canManage, sx }: StudentFormSummaryProps) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [form, setForm] = useState<StudentForm | null>(null)
  const [editOpen, setEditOpen] = useState(false)

  // Языковой профиль грузится здесь, а не в разделе цели: правит их одно окно,
  // и владелец данных должен быть один — иначе после сохранения обновилась бы
  // половина блока.
  const [profile, setProfile] = useState<StudentLanguageProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [profileFailed, setProfileFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setFailed(false)

    void (async () => {
      try {
        const data = await studentFormsApi.getForm(contractId)
        if (!cancelled) setForm(data)
      } catch {
        // Анкета — дополнительный блок: ошибка загрузки не ломает профиль.
        // Но и «не заполнена» показывать нельзя — учитель решит, что данных нет.
        if (!cancelled) {
          setForm(null)
          setFailed(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [contractId])

  useEffect(() => {
    let cancelled = false
    setProfileLoading(true)
    setProfileFailed(false)

    void (async () => {
      try {
        const data = await languageProfileApi.show(userId)
        if (!cancelled) setProfile(data)
      } catch {
        // Раздел дополнительный: ошибка не должна ломать анкету. Но и «не
        // заполнено» показывать нельзя — учитель решит, что данных нет.
        if (!cancelled) {
          setProfile(null)
          setProfileFailed(true)
        }
      } finally {
        if (!cancelled) setProfileLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [userId])

  const filled = useMemo(() => {
    if (!form) return []
    const byKey = new Map(form.fields.map((field) => [field.key, field]))

    return form.answers
      .map((answer) => ({ field: byKey.get(answer.field_key), value: answer.value }))
      .filter(
        (row): row is { field: StudentFormField; value: StudentFormAnswerValue } =>
          row.field !== undefined && !isEmptyValue(row.value),
      )
      .sort((a, b) => a.field.sort_order - b.field.sort_order)
  }, [form])

  // Одна кнопка на весь блок: цель и поля анкеты правятся в одном окне.
  // Ученику она нужна, даже если полей анкеты ему не досталось — цель он
  // заполняет всегда, это его цель.
  const canEdit =
    (!failed
      && (canManage || (form?.fields ?? []).some((field) => field.access === 'student_editable')))
    || !profileFailed

  return (
    <Card sx={{ mt: 3, ...sx }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('studentForms.title')}</Typography>
          {canEdit && !loading && !profileLoading && (
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditOpen(true)}
            >
              {filled.length > 0 ? t('common.edit', 'Редактировать') : t('studentForms.fill')}
            </Button>
          )}
        </Box>

        {/* Цель, расписание и уровень — из языкового профиля. Он принадлежит
            человеку, а не договору, поэтому в каталог полей анкеты не входит,
            но заполняется здесь же: второй экран ради двух полей не нужен. */}
        <LanguageGoalSection profile={profile} loading={profileLoading} failed={profileFailed} />

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : failed ? (
          <Typography variant="body2" color="text.secondary">
            {t('studentForms.loadError')}
          </Typography>
        ) : filled.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('studentForms.summaryEmpty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filled.map(({ field, value }) => (
              <Box key={field.key}>
                <Typography variant="body2" color="text.secondary">
                  {field.label}
                </Typography>
                {field.type === 'rating' && typeof value === 'number' ? (
                  <Rating value={value} readOnly size="small" />
                ) : (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {formatValue(field, value, {
                      yes: t('common.yes', 'Да'),
                      no: t('common.no', 'Нет'),
                    })}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <StudentFormEditDialog
        open={editOpen}
        contractId={contractId}
        userId={userId}
        form={form}
        profile={profile}
        canManage={canManage}
        onClose={() => setEditOpen(false)}
        onSaved={({ form: nextForm, profile: nextProfile }) => {
          if (nextForm !== undefined) setForm(nextForm)
          if (nextProfile !== undefined) setProfile(nextProfile)
        }}
      />
    </Card>
  )
}

/** Незаполненное поле: null, пустая строка, пустой список, пустая пара языков. */
function isEmptyValue(value: StudentFormAnswerValue): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') {
    const pair = value as { from?: string; to?: string }
    return !pair.from?.trim() && !pair.to?.trim()
  }
  return false
}

function formatValue(
  field: StudentFormField,
  value: StudentFormAnswerValue,
  booleanLabels: { yes: string; no: string },
): string {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') {
    return value ? booleanLabels.yes : booleanLabels.no
  }
  if (typeof value === 'object' && value !== null) {
    const pair = value as { from?: string; to?: string }
    return [pair.from, pair.to].filter(Boolean).join(' → ')
  }
  if (field.type === 'date' && typeof value === 'string') return formatDateDMY(value)
  return String(value)
}
